/**
 * @file components/chat/CompactModelSelector.jsx
 * Uses useChatSession
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useChatSession } from '../../context/ChatSessionContext';
import { useModelCatalog } from '../../context/ModelCatalogContext';
import styles from '../../styles/ChatApp.module.css';
import ModelIcon from '../common/ModelIcon';
import { ChevronDown } from 'lucide-react';

const CompactModelSelector = () => {
    const { selectedModel, setSelectedModel, mustAutoroute } = useChatSession();
    const { proprietaryModels, providerGroups, otherGroupLabel } = useModelCatalog();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    // Build the same grouped structure WelcomeModelSelector uses, from the
    // live catalog. Supports both {prefix} and {models} group shapes.
    const groupedModels = useMemo(() => {
        const claimed = new Set(
            providerGroups.flatMap(({ prefix, models }) =>
                models ? models : (prefix ? proprietaryModels.filter(m => m.startsWith(prefix)) : [])
            )
        );
        return [
            ...providerGroups.map(({ label, prefix, models }) => ({
                label,
                models: models ?? proprietaryModels.filter(m => m.startsWith(prefix)),
            })),
            {
                label: otherGroupLabel,
                models: proprietaryModels.filter(m => !claimed.has(m)),
            },
        ].filter(g => g.models.length > 0);
    }, [providerGroups, proprietaryModels, otherGroupLabel]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (model) => {
        setSelectedModel(model);
        setIsOpen(false);
    };

    return (
        <div className={styles.compactSelectorWrapper} ref={menuRef}>
            <button
                type="button"
                className={styles.compactSelectorButton}
                onClick={() => setIsOpen(prev => !prev)}
            >
                <ModelIcon modelName={selectedModel} size={16} />
                <span>{selectedModel}</span>
                <ChevronDown
                    size={16}
                    className={`${styles.compactSelectorArrow} ${isOpen ? styles.open : ''}`}
                />
            </button>

            {isOpen && (
                <div className={styles.modelPopupMenu}>
                    {/* Route AI sits above the groups as a standalone entry */}
                    <div
                        className={`${styles.modelPopupItem} ${selectedModel === 'Route AI' ? styles.activePopupItem : ''}`}
                        onClick={() => handleSelect('Route AI')}
                    >
                        <ModelIcon modelName="Route AI" size={16} />
                        <span>Route AI</span>
                    </div>

                    {/* Provider groups with divider labels */}
                    {groupedModels.map(({ label, models }) => (
                        <React.Fragment key={label}>
                            <div className={styles.modelPopupGroupLabel}>{label}</div>
                            {models.map(model => {
                                // Pins are disabled for credit-funded users
                                // (mustAutoroute) — the backend rejects them.
                                const disabled = mustAutoroute;
                                const title = mustAutoroute
                                    ? 'Add your own API keys to pick a model'
                                    : undefined;
                                return (
                                    <div
                                        key={model}
                                        className={`${styles.modelPopupItem} ${selectedModel === model ? styles.activePopupItem : ''} ${disabled ? styles.modelPopupItemDisabled : ''}`}
                                        onClick={() => !disabled && handleSelect(model)}
                                        title={title}
                                    >
                                        <ModelIcon modelName={model} size={16} />
                                        <span>{model}</span>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CompactModelSelector;
